// Considered: ContextLevelSummaryAtom — rejected because it renders a compact inline summary chip, not a full interactive flow-canvas card with header/filter-rows/sample. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import type { ContextFlowLevel } from '../../../types/contextFlow';
import { readFilterStringValues } from '../../../utils/readFilterStringValues';
import './ContextFlowNodeMolecule.scss';

export interface ContextFlowNodeMoleculeProps {
  level: ContextFlowLevel;
  order: number;
  active?: boolean;
  readOnly?: boolean;
  /**
   * EAINT-862 Phase 3: renders as a dashed, non-interactive preview node for an agent-proposed
   * level not yet part of the saved walk — disabled (no select/reorder), never `active`.
   */
  ghost?: boolean;
  onClick?: () => void;
  /** Testid suffix, e.g. the level id */
  'data-testid'?: string;
}

export interface ContextFlowRootNodeProps {
  active?: boolean;
  onClick?: () => void;
  'data-testid'?: string;
}

/** Root flow node */
export const ContextFlowRootNodeMolecule: React.FC<ContextFlowRootNodeProps> = ({
  active,
  onClick,
  'data-testid': testId = 'cf-node-root',
}) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`context-flow-node-molecule context-flow-node-molecule--root${active ? ' context-flow-node-molecule--active' : ''}`}
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
    >
      <div className="context-flow-node-molecule__header">
        <span className="context-flow-node-molecule__root-icon" aria-hidden="true">
          <i className="pi pi-circle-on" />
        </span>
        <span className="context-flow-node-molecule__label">
          {t('context.editor.canvas.rootNode.title')}
        </span>
      </div>
      <div className="context-flow-node-molecule__root-desc">
        {t('context.editor.canvas.rootNode.description')}
      </div>
    </button>
  );
};

/** Level flow node */
const ContextFlowNodeMolecule: React.FC<ContextFlowNodeMoleculeProps> = ({
  level,
  order,
  active = false,
  readOnly = false,
  ghost = false,
  onClick,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const dirClass = `context-flow-node-molecule--${level.direction}`;

  // The `type` filter is stored inline in the level's property filters.
  const typeFilter = (level.filters?.propertyFilters ?? []).find(
    (f) => f.property === 'type'
  );
  const typeValue = typeFilter ? readFilterStringValues(typeFilter).join(', ') : '';

  return (
    <button
      type="button"
      className={`context-flow-node-molecule ${dirClass}${active ? ' context-flow-node-molecule--active' : ''}${readOnly ? ' context-flow-node-molecule--readonly' : ''}${ghost ? ' context-flow-node-molecule--ghost' : ''}`}
      onClick={ghost ? undefined : onClick}
      disabled={ghost}
      data-testid={testId ?? `cf-node-${level.id}`}
      aria-pressed={active}
    >
      {/* Header row */}
      <div className="context-flow-node-molecule__header">
        <span className="context-flow-node-molecule__order-badge" aria-hidden="true">
          {order}
        </span>
        {level.icon && (
          <i
            className={`pi ${level.icon} context-flow-node-molecule__level-icon`}
            aria-hidden="true"
          />
        )}
        <span className="context-flow-node-molecule__label">{level.label || t('context.editor.canvas.untitledLevel')}</span>
        <ChipAtom
          label={level.direction === 'outwards'
            ? t('context.editor.flow.directionOut')
            : t('context.editor.flow.directionIn')}
          className={`context-flow-node-molecule__dir-chip context-flow-node-molecule__dir-chip--${level.direction}`}
        />
      </div>

      {/* Type filter row */}
      {typeValue && (
        <div className="context-flow-node-molecule__filter-row">
          <i className="pi pi-tag context-flow-node-molecule__filter-icon" aria-hidden="true" />
          <span className="context-flow-node-molecule__filter-key">{t('context.editor.flow.typeLabel')}</span>
          <span className="context-flow-node-molecule__filter-op">{t('context.editor.flow.isOp')}</span>
          <span className="context-flow-node-molecule__filter-val">{typeValue}</span>
        </div>
      )}

      {/* Repeat badge */}
      {level.repeat > 1 && (
        <span className="context-flow-node-molecule__repeat-badge">
          <i className="pi pi-replay" aria-hidden="true" />
          ×{level.repeat}
        </span>
      )}

      {/* Segments sub-list */}
      {level.segments && level.segments.length > 0 && (
        <div className="context-flow-node-molecule__segments-group">
          <div className="context-flow-node-molecule__segments-header">
            <span className="context-flow-node-molecule__segments-eyebrow">
              {t('context.editor.flow.segments')} · {level.segments.length}
            </span>
            <ChipAtom
              label={t('context.editor.flow.split')}
              className="context-flow-node-molecule__split-chip"
            />
          </div>
          <ul className="context-flow-node-molecule__segments">
            {level.segments.map((seg) => (
              <li
                key={seg.id}
                className="context-flow-node-molecule__segment-row"
              >
                <span className="context-flow-node-molecule__seg-label">{seg.label.default}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </button>
  );
};

export default ContextFlowNodeMolecule;
